import { alpha, Box, LinearProgress, type SxProps, type Theme, Typography } from "@mui/material";
import { Duration } from "luxon";
import { type FC, useEffect, useState } from "react";
import { ALPHA, FONT_SIZE, INK, LONG_TASK_CARD, MOTION, RADIUS, SHADOW } from "../../../theme/tokens";
import { PulseDot } from "../Animations/PulseDot";

type Props = {
	/** Already-translated: what is happening ("Annie is designing your printout"). Announced politely. */
	title: string;
	/** Already-translated: how long it usually takes ("Up to about 2 minutes"), said up front. Optional. */
	expectation?: string;
	/** Counts the time since the card mounted (m:ss) after `expectation`. Mount the card per job (`key`) to restart it. */
	showElapsed: boolean;
	dataTestId?: string;
	/** Placement only (e.g. `position: absolute` over a preview); the surface is the component's. */
	sx?: SxProps<Theme>;
};

/**
 * The floating "still working on it" card for a job that takes long enough to need saying so — an AI design, an
 * import — laid over the surface it will replace, which stays visible underneath. A pulsing dot, the title, the
 * expected wait with the time so far, and an indeterminate progress hairline along its foot. The title is a polite
 * live region; the ticking clock is hidden from assistive tech so it isn't read out every second.
 *
 * Enters with a short fade and lift (transform/opacity only); reduced motion drops the movement and the
 * progress animation's travel is left to MUI, which honors the OS setting.
 */
export const LongTaskStatusCard: FC<Props> = ({ title, expectation, showElapsed, dataTestId, sx }) => {
	const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
	useEffect(() => {
		if (!showElapsed) return;
		const timer = setInterval(() => setElapsedSeconds((seconds) => seconds + 1), LONG_TASK_CARD.ELAPSED_TICK_MS);
		return () => clearInterval(timer);
	}, [showElapsed]);
	const elapsed = Duration.fromObject({ seconds: elapsedSeconds }).toFormat("m:ss");

	return (
		<Box
			data-testid={dataTestId}
			sx={[
				(theme) => ({
					minWidth: LONG_TASK_CARD.MIN_WIDTH_PX,
					// Never wider than the surface it floats over, with a margin either side.
					maxWidth: `calc(100% - ${theme.spacing(4)})`,
					overflow: "hidden",
					bgcolor: "background.paper",
					border: `1px solid ${alpha(theme.palette.primary.main, ALPHA.FIELD_OUTLINE)}`,
					borderRadius: `${RADIUS.POPOVER}px`,
					boxShadow: SHADOW.POPOVER_FLOAT,
					"@keyframes longTaskCardIn": { from: { opacity: 0, transform: "translateY(-6px)" }, to: { opacity: 1, transform: "none" } },
					animation: `longTaskCardIn ${MOTION.DURATION.WINDOW_ENTER}ms ${MOTION.EASE_OUT}`,
					"@media (prefers-reduced-motion: reduce)": { animation: "none" },
				}),
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25 }}>
				<PulseDot />
				<Box sx={{ minWidth: 0 }}>
					<Typography role="status" aria-live="polite" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY }}>
						{title}
					</Typography>
					{(expectation !== undefined || showElapsed) && (
						<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, fontVariantNumeric: "tabular-nums" }}>
							{expectation}
							{expectation !== undefined && showElapsed && " · "}
							{showElapsed && (
								<Box component="span" aria-hidden data-testid={dataTestId === undefined ? undefined : `${dataTestId}-elapsed`}>
									{elapsed}
								</Box>
							)}
						</Typography>
					)}
				</Box>
			</Box>
			<LinearProgress aria-hidden sx={{ height: LONG_TASK_CARD.PROGRESS_HEIGHT_PX, bgcolor: "transparent" }} />
		</Box>
	);
};
